import { StyledRange } from "@silevis/reactgrid";
import { investorType } from "../../../interfaces/interfaces";
import dayjs from "dayjs";
import moment from "moment";

// don't change the column names, as they are used for key based index from the data
// so if we need to change the column names, we need to change the data as well, therefore change the enum ColumnTitle which is below this enum definition
export enum investmentsColumns {
  round = "round",
  investmentDate = "investmentDate",
  investor = "investor",
  keyInvestors = "keyInvestors",
  investmentAmount = "investmentAmount",
}

export enum investmentsColumnTitle {
  round = "Round",
  investmentDate = "Investment Date",
  investor = "Investor",
  keyInvestors = "Key Investors",
  investmentAmount = "Investment Amount (₹)",
}

export const investmentsHeaders = [
  investmentsColumns.round,
  investmentsColumns.investmentDate,
  investmentsColumns.investor,
  investmentsColumns.keyInvestors,
  investmentsColumns.investmentAmount,
];

export const investmentsHeadersObj: Record<string, any> = {
  [investmentsColumns.round]: {
    title: investmentsColumnTitle.round,
    styles: {
      justifyContent: "flex-start",
      maxWidth: "25px !important",
    },
    type: "noneditable",
    isTotal: false,
    width: 20,
  },
  [investmentsColumns.investmentDate]: {
    title: investmentsColumnTitle.investmentDate,
    styles: {
      justifyContent: "center",
    },
    type: "date",
    isTotal: false,
    width: 224,
  },
  [investmentsColumns.investor]: {
    title: investmentsColumnTitle.investor,
    styles: {
      justifyContent: "flex-start",
    },
    type: "select",
    isTotal: false,
    width: 180,
    options: [
      { label: "Angel", value: investorType.angel },
      { label: "Venture Capital", value: investorType.vc },
      { label: "Accelerator", value: investorType.accelerator },
    ],
    optionLabelKey: "label",
  },
  [investmentsColumns.keyInvestors]: {
    title: investmentsColumnTitle.keyInvestors,
    styles: {
      justifyContent: "flex-start",
    },
    type: "string",
    isTotal: false,
    width: 400,
  },
  [investmentsColumns.investmentAmount]: {
    title: investmentsColumnTitle.investmentAmount,
    styles: {
      justifyContent: "flex-end",
    },
    type: "number",
    format: new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }),
    isTotal: true,
    width: 166,
  },
};

export const getInitialInvestmentRounds = (): {
  headers: any[];
  rowData: any[];
  footer: any;
} => {
  const rowData = [];
  const initialRow = {};
  investmentsHeaders.forEach((header, index) => {
    if (index == 0) {
      initialRow[header] = 1; // Start with round 1
    } else {
      initialRow[header] = "";
    }
  });
  rowData.push(initialRow);

  const initalFooter = {};
  investmentsHeaders.forEach((header, index) => {
    initalFooter[header] = index === 0 ? "Total" : "-";
  });

  const headers = [];
  investmentsHeaders.forEach((header) => {
    headers.push({
      key: header,
      value: investmentsHeadersObj[header].title,
    });
  });

  // const rowData = [[1, ...Array(investmentsHeaders.length - 1).fill("")]];
  // const footer = ["Total", ...Array(investmentsHeaders.length - 1).fill("-")];

  return {
    headers,
    rowData: rowData,
    footer: initalFooter,
  };
};

export const onAddInitialInvestmentRow = () => {
  const initialRow = {};
  investmentsHeaders.forEach((header) => {
    initialRow[header] = "";
  });
  return initialRow;
};

export const getInvestmentsGridInvalidRange = (gridData: any) => {
  let missingDataErrors: StyledRange[] = [];
  let dateErrors: StyledRange[] = [];
  let fullGridError: StyledRange[] = [];

  const filteredHeaders = investmentsHeaders.reduce((prev, curVal) => {
    if (curVal !== investmentsColumns.round) {
      prev.push(curVal);
    }
    return prev;
  }, []);

  const isGridEmpty = gridData.rowData.every((row) =>
    // ignore the rounds column (first column)
    filteredHeaders.every((headerKey) => {
      return row[headerKey]?.toString().trim() === "";
    })
  );

  if (isGridEmpty) {
    fullGridError.push({
      range: {
        start: { rowIndex: 1, columnIndex: 1 }, // Start after the first column
        end: {
          rowIndex: gridData.rowData.length + 1,
          columnIndex: gridData.headers.length,
        },
      },
      styles: { background: "#FDF1F1" }, // Light red for fully empty grid
    });

    return { missingDataErrors, dateErrors, fullGridError };
  }

  gridData.rowData.forEach((row, rowIndex) => {
    // ✅ **Step 1: Check if the row contains any data (excluding the first column)**

    const hasData = filteredHeaders.some((headerKey) => {
      return row[headerKey]?.toString().trim() !== "";
    });

    if (!hasData) return; // Skip empty rows

    investmentsHeaders.forEach((headerKey, colIndex) => {
      const cell = row[headerKey] || ""; // Get the cell value, default to empty string if undefined
      // const columnKey = gridData.headers[colIndex];
      const columnType = investmentsHeadersObj[headerKey]?.type;

      // ✅ **Step 2: Check for Missing Data in Non-Empty Rows (Except First Column)**
      if (hasData && colIndex != 0 && cell.toString().trim() === "") {
        missingDataErrors.push({
          range: {
            start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
            end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
          },
          styles: { background: "#FDF1F1" }, // Light red for missing data
        });
      }

      // ✅ **Step 3: Validate Date Format if a Date is Entered**
      if (columnType === "date" && cell.toString().trim() !== "") {
        const enteredDate = moment(cell, "DD/MM/YYYY"); // Strict parsing
        // console.log(
        //   "enteredDate",
        //   enteredDate,
        //   cell,
        //   dayjs("22/10/2023", "DD/MM/YYYY", true)
        // );
        if (!enteredDate.isValid()) {
          dateErrors.push({
            range: {
              start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
              end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
            },
            styles: { background: "#FDF1F1" }, // Light red for invalid date
          });
        }
      }
    });
  });

  return { missingDataErrors, dateErrors, fullGridError };
};

export const getFirstInvestmentGridError = (gridData: any) => {
  // ✅ Check if the entire grid is empty first

  const filteredHeaders = investmentsHeaders.reduce((prev, curVal) => {
    if (curVal !== investmentsColumns.round) {
      prev.push(curVal);
    }
    return prev;
  }, []);
  const isGridEmpty = gridData.rowData.every((row) =>
    // ignore the rounds column (first column)
    filteredHeaders.every((headerKey) => {
      return row[headerKey]?.toString().trim() === "";
    })
  );
  if (isGridEmpty) {
    return {
      range: {
        start: { rowIndex: 1, columnIndex: 1 },
        end: {
          rowIndex: gridData.rowData.length + 1,
          columnIndex: gridData.headers.length,
        },
      },
      styles: { background: "#F8D7DA" }, // Light red for fully empty grid
    };
  }

  // ✅ Traverse only until the first error is found
  for (let rowIndex = 0; rowIndex < gridData.rowData.length; rowIndex++) {
    const row = gridData.rowData[rowIndex];
    const hasData = investmentsHeaders.slice(1).some((headerKey) => {
      return row[headerKey]?.toString().trim() !== "";
    });

    if (!hasData) continue; // Skip fully empty rows

    for (let colIndex = 1; colIndex < row.length; colIndex++) {
      const cell = row[colIndex];
      const columnKey = gridData.headers[colIndex];
      const columnType = investmentsHeadersObj[columnKey]?.type;

      // ✅ First Missing Data Error (Only check if row has any data)
      if (hasData && cell.toString().trim() === "") {
        return {
          range: {
            start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
            end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
          },
          styles: { background: "#FDF1F1" }, // Light red for missing data
        };
      }

      // ✅ First Date Error (Invalid Date Format)
      if (columnType === "date" && cell.toString().trim() !== "") {
        const enteredDate = moment(cell, "DD/MM/YYYY"); // Strict parsing
        if (!enteredDate.isValid()) {
          return {
            range: {
              start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
              end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
            },
            styles: { background: "#FDF1F1" }, // Light red for invalid date
          };
        }
      }
    }
  }

  // ✅ If no errors found, return null
  return null;
};

export const investmentFormValidation = (values) => {
  const errors = {};

  const missingData = [];
  console.log("values", values);

  if (!values?.raisedInvestment) {
    errors["raisedInvestment"] = "Select an option";
    missingData.push("Select an option");
  } else {
    if (values.raisedInvestment === "yes") {
      if (!values?.investmentRounds) {
        errors["investmentRounds"] = "Details Missing";
        missingData.push("Details Missing");
      } else {
        const investmentGridError = getFirstInvestmentGridError(
          values.investmentRounds
        );
        if (investmentGridError) {
          errors["investmentRounds"] = "Details Missing";
          missingData.push("Details Missing");
        }
      }
    }
  }
  console.log("investment form validation", errors, missingData);
  return {
    errors: errors,
    missingData: missingData,
  };
};
